import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Loader2, Lock, Unlock } from "lucide-react";

export default function EditQRDialog({ target, onOpenChange, onSaved }) {
  const open = !!target;
  const [label, setLabel] = useState("");
  const [url, setUrl] = useState("");
  const [locked, setLocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unlocking, setUnlocking] = useState(false);

  useEffect(() => {
    if (target) {
      setLabel(target.label || "");
      setUrl(target.destination_url || "");
      setLocked(!!target.locked);
    }
  }, [target]);

  const save = async (e) => {
    e.preventDefault();
    if (!target) return;
    setLoading(true);
    try {
      await api.patch(`/qrs/${target.id}`, { label, destination_url: url });
      toast.success("Perubahan tersimpan");
      onSaved?.();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const unlock = async () => {
    if (!target) return;
    setUnlocking(true);
    try {
      await api.post(`/qrs/${target.id}/unlock`);
      setLocked(false);
      toast.success(`QR ${target.code} dibuka`);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setUnlocking(false);
    }
  };

  const lockNow = async () => {
    if (!target) return;
    if (!url.trim()) {
      toast.error("Isi link tujuan sebelum mengunci");
      return;
    }
    setUnlocking(true);
    try {
      // save first to persist current values
      await api.patch(`/qrs/${target.id}`, { label, destination_url: url });
      await api.post(`/qrs/${target.id}/lock`);
      toast.success(`QR ${target.code} disimpan & dikunci`);
      onSaved?.();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setUnlocking(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="edit-qr-dialog">
        <DialogHeader>
          <DialogTitle className="jscan-brand text-2xl">
            Edit QR{" "}
            <span className="jscan-mono text-sky-600 dark:text-sky-400 text-base">
              {target?.code}
            </span>
          </DialogTitle>
          <DialogDescription>
            Ubah label internal dan link Google Maps review UMKM.
          </DialogDescription>
        </DialogHeader>

        {locked && (
          <div
            className="flex items-start justify-between gap-3 rounded-lg border border-rose-500/30 bg-rose-500/10 p-3"
            data-testid="edit-locked-banner"
          >
            <div className="flex gap-2 text-sm text-rose-700 dark:text-rose-300">
              <Lock className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-medium">QR terkunci</div>
                <div className="text-xs opacity-80">
                  Buka kunci dulu untuk mengubah tujuan.
                </div>
              </div>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={unlock}
              disabled={unlocking}
              data-testid="edit-unlock-button"
              className="rounded-full"
            >
              {unlocking ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <>
                  <Unlock className="w-3.5 h-3.5 mr-1.5" /> Buka kunci
                </>
              )}
            </Button>
          </div>
        )}

        <form onSubmit={save} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-label">Label toko</Label>
            <Input
              id="edit-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="mis. Bebek Kaleyo Kelapa Gading"
              disabled={locked}
              data-testid="edit-label-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-url">Link Google Maps review</Label>
            <Input
              id="edit-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://g.page/r/..."
              disabled={locked}
              data-testid="edit-url-input"
            />
          </div>
          <DialogFooter className="gap-2 sm:justify-between flex-col sm:flex-row">
            <Button
              type="button"
              variant="outline"
              onClick={lockNow}
              disabled={locked || unlocking || loading || !url.trim()}
              className="rounded-full"
              data-testid="save-and-lock-button"
            >
              <Lock className="w-4 h-4 mr-2" /> Simpan & kunci
            </Button>
            <Button
              type="submit"
              disabled={locked || loading}
              className="rounded-full bg-sky-600 hover:bg-sky-700"
              data-testid="save-edit-button"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Simpan
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
